import axios from "axios";
import { API_BASE } from "../config/api";

export function generateVerificationCode() {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

/**
 * Normalize to international Somalia format with 252 prefix.
 * Accepts: 619006649, 0619006649, 61619006649, 252619006649, +252619006649
 */
const SOMALIA_MOBILE_PREFIXES = [
  "61",
  "62",
  "63",
  "65",
  "66",
  "68",
  "69",
  "71",
  "77",
  "90",
];

function stripDuplicateSomaliaPrefix(digits) {
  let result = String(digits || "");
  if (!result) return result;

  for (const prefix of SOMALIA_MOBILE_PREFIXES) {
    while (result.startsWith(prefix + prefix)) {
      result = result.slice(prefix.length);
    }

    if (
      result.length === prefix.length * 2 + 7 &&
      result.startsWith(prefix) &&
      result.slice(prefix.length).startsWith(prefix)
    ) {
      result = result.slice(prefix.length);
    }
  }

  return result;
}

export function formatPhoneForWhatsApp(phoneNumber, countryCode = "+252") {
  const codeDigits = String(countryCode || "+252").replace(/\D/g, "") || "252";
  let digits = String(phoneNumber || "").replace(/\D/g, "");

  if (!digits) return "";

  if (digits.startsWith("00")) {
    digits = digits.substring(2);
  }

  while (digits.startsWith(codeDigits + codeDigits)) {
    digits = digits.substring(codeDigits.length);
  }

  if (digits.startsWith(codeDigits)) {
    digits = digits.substring(codeDigits.length);
  }

  if (digits.startsWith("0")) {
    digits = digits.substring(1);
  }

  digits = stripDuplicateSomaliaPrefix(digits);

  const full = `${codeDigits}${digits}`;

  if (codeDigits === "252" && !/^252[1-9]\d{8}$/.test(full)) {
    return "";
  }

  return full;
}

export function buildOtpMessage(code, userType = "User", isResend = false) {
  const intro = isResend
    ? "Your new verification code is"
    : "Your verification code is";
  return `*CAAFICare Verification*\n\n${intro}: *${code}*\n\nUser Type: ${userType}\n\nThis code will expire in 10 minutes.`;
}

export async function sendOtpWhatsApp({
  phoneNumber,
  countryCode = "+252",
  code,
  userType = "User",
  isResend = false,
}) {
  const cleanPhone = formatPhoneForWhatsApp(phoneNumber, countryCode);
  if (!cleanPhone) {
    throw new Error(
      "Invalid phone number. Enter 9 digits like 619006649 or 634773173 (do not repeat 61/63 if +252 is selected)."
    );
  }

  const message = buildOtpMessage(code, userType, isResend);

  try {
    const response = await axios.post(`${API_BASE}/otp/send`, {
      phone: cleanPhone,
      countryCode,
      code,
      userType,
      isResend,
    });

    if (!response.data?.success) {
      throw new Error(
        response.data?.message ||
          response.data?.error ||
          "Failed to send WhatsApp verification code"
      );
    }

    return { success: true, phone: response.data.phone || cleanPhone };
  } catch (otpError) {
    const status = otpError.response?.status;
    if (status && status !== 404 && status !== 405) {
      throw new Error(
        otpError.response?.data?.message ||
          otpError.response?.data?.error ||
          otpError.message ||
          "Failed to send WhatsApp verification code"
      );
    }

    const fallback = await axios.post(`${API_BASE}/whatsApp`, {
      phone: cleanPhone,
      message,
    });

    if (!fallback.data?.success) {
      throw new Error(
        fallback.data?.error ||
          fallback.data?.details ||
          fallback.data?.message ||
          "Failed to send WhatsApp verification code"
      );
    }

    return { success: true, phone: fallback.data.phone || cleanPhone };
  }
}
